import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Alert,
  Fab,
  Dialog,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Chip,
  Avatar,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SchoolIcon from '@mui/icons-material/School';
import api from '../../services/api';
import '../../styles/courses.css';
import CourseForm from './components/CourseForm';

export default function Courses() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [courseDialog, setCourseDialog] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);

  useEffect(() => {
    loadCourses();
    loadCompanies();
  }, []);

  async function loadCourses() {
    try {
      setLoading(true);
      const res = await api.get('/courses');
      setCourses(res.data);
    } catch (err) {
      console.error('Erro ao carregar cursos', err);
      setError('Não foi possível carregar os cursos.');
    } finally {
      setLoading(false);
    }
  }

  async function loadCompanies() {
    try {
      const res = await api.get('/companies');
      setCompanies(res.data);
    } catch (err) {
      console.error('Erro ao carregar empresas', err);
    }
  }

  const handleCreateCourse = () => {
    setEditingCourse(null);
    setCourseDialog(true);
  };

  const handleSaveCourse = async formData => {
    setLoading(true);
    setError(null);
    try {
      if (editingCourse) {
        await api.put(`/courses/${editingCourse.id}`, formData);
        setSuccess('Curso atualizado com sucesso');
      } else {
        await api.post('/courses', formData);
        setSuccess('Curso criado com sucesso');
      }
      loadCourses();
      setCourseDialog(false);
    } catch (err) {
      setError('Erro ao salvar curso');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCourse = async (courseId, courseTitle) => {
    if (!window.confirm(`Excluir curso "${courseTitle}"?`)) return;
    try {
      setLoading(true);
      await api.delete(`/courses/${courseId}`);
      setSuccess('Curso excluído com sucesso');
      loadCourses();
    } catch (err) {
      setError('Erro ao excluir curso');
    } finally {
      setLoading(false);
    }
  };

  const handleViewModules = courseId => {
    navigate(`/courses/${courseId}/modules`);
  };

  if (loading && courses.length === 0) {
    return (
      <Box
        className="courses-page"
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 2 }}>
            Carregando cursos...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="courses-page" sx={{ p: 3, backgroundColor: 'transparent', minHeight: '100vh' }}>
      {/* Header */}
      <Box
        className="courses-header"
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}
      >
        <Typography
          className="courses-title"
          variant="h4"
          component="h1"
          sx={{ color: '#ffffff', fontWeight: 700 }}
        >
          Gerenciamento de Cursos
        </Typography>
        <Fab
          className="add-course-fab"
          color="primary"
          aria-label="add course"
          onClick={handleCreateCourse}
        >
          <AddIcon />
        </Fab>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: '12px',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            color: '#fca5a5',
            border: '1px solid rgba(239, 68, 68, 0.3)',
          }}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{
            mb: 2,
            borderRadius: '12px',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            color: '#86efac',
            border: '1px solid rgba(16, 185, 129, 0.3)',
          }}
        >
          {success}
        </Alert>
      )}

      {/* Courses Grid */}
      {courses.length === 0 ? (
        <Card
          sx={{
            backgroundColor: 'rgba(30, 41, 59, 0.4)',
            border: '2px dashed rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            textAlign: 'center',
            py: 6,
          }}
        >
          <Typography sx={{ fontSize: '3rem', mb: 2 }}></Typography>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 600, mb: 1 }}>
            Nenhum curso criado
          </Typography>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', mb: 3 }}>
            Clique no botão + para criar seu primeiro curso
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateCourse}
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
              borderRadius: '8px',
              fontWeight: 600,
            }}
          >
            Criar Curso
          </Button>
        </Card>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              md: 'repeat(auto-fill, minmax(300px, 1fr))',
            },
            gap: 2,
          }}
        >
          {courses.map(course => (
            <Card
              key={course.id}
              className="course-card"
              sx={{
                backgroundColor: 'rgba(30, 41, 59, 0.6)',
                borderColor: 'rgba(59, 130, 246, 0.2)',
                border: '1px solid',
                color: '#ffffff',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 32px rgba(59, 130, 246, 0.2)',
                },
              }}
            >
              <CardContent
                className="course-card-content"
                sx={{ backgroundColor: 'rgba(30, 41, 59, 0.6)' }}
              >
                {/* Course Header */}
                <Box className="course-header" sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Avatar sx={{ backgroundColor: '#3b82f6', width: 48, height: 48 }}>
                    <SchoolIcon />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      component="h2"
                      sx={{ color: '#ffffff', fontWeight: 600 }}
                    >
                      {course.title}
                    </Typography>
                    <Typography
                      className="course-company"
                      variant="body2"
                      sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                    >
                      {course.company?.name}
                    </Typography>
                  </Box>
                </Box>

                {/* Description */}
                <Typography
                  className="course-description"
                  variant="body2"
                  sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2, minHeight: '40px' }}
                >
                  {course.description}
                </Typography>

                {/* Stats */}
                <Box
                  className="course-chips"
                  sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}
                >
                  <Chip
                    label={`${course._count?.modules || 0} módulos`}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(59, 130, 246, 0.2)',
                      color: '#3b82f6',
                    }}
                  />
                  <Chip
                    label={`${course._count?.enrollments || 0} matriculados`}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(6, 182, 212, 0.2)',
                      color: '#06b6d4',
                    }}
                  />
                </Box>

                {/* Author */}
                <Typography
                  className="course-author"
                  variant="body2"
                  sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.85rem' }}
                >
                  Criado por {course.createdBy?.fullName}
                </Typography>
              </CardContent>

              {/* Actions */}
              <CardActions
                className="course-actions"
                sx={{
                  backgroundColor: 'rgba(30, 41, 59, 0.8)',
                  borderTop: '1px solid rgba(59, 130, 246, 0.1)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  p: 2,
                }}
              >
                <Button
                  className="manage-btn"
                  size="small"
                  onClick={() => handleViewModules(course.id)}
                  sx={{
                    color: '#3b82f6',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    },
                  }}
                >
                  Gerenciar
                </Button>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    className="action-icon-btn"
                    size="small"
                    sx={{
                      color: '#3b82f6',
                      '&:hover': { backgroundColor: 'rgba(59, 130, 246, 0.1)' },
                    }}
                    onClick={() => {
                      setEditingCourse(course);
                      setCourseDialog(true);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    className="action-icon-btn delete-btn"
                    size="small"
                    sx={{
                      color: '#ef4444',
                      '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)' },
                    }}
                    onClick={() => handleDeleteCourse(course.id, course.title)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </CardActions>
            </Card>
          ))}
        </Box>
      )}

      {/* Course Form Dialog */}
      <Dialog
        open={courseDialog}
        onClose={() => setCourseDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
          },
        }}
      >
        <CourseForm
          course={editingCourse}
          companies={companies}
          onSave={handleSaveCourse}
          onCancel={() => setCourseDialog(false)}
          loading={loading}
        />
      </Dialog>
    </Box>
  );
}
